import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { axiosCoreInstance } from "src/config/axios";
import { useTranslation } from "react-i18next";
import { RQ_COMPANY_DETAILS_KEY } from "src/modules/company/hooks/useCompanyDetails";
import { FormErrorObject } from "src/utils/types";
import { Storage } from "src/utils/storage";
import { RQ_COMPANY_LIST_KEY } from "./useCompanyList";

interface OwnProps {
    onSuccess?: () => void;
    onError?: (errors: FormErrorObject) => void;
}

export interface CompanyDataRequest {
    name: string;
    vatId: string | null;
    address: {
        address1: string;
        address2: string | null;
        city: string;
        postalCode: string;
    } | null;
    eipaUsername: string | null;
    eipaPassword: string | null;
    updateEipaPassword: boolean;
}

export const userCompanyDataMutation = ({ onSuccess, onError }: OwnProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const currentCompany = Storage.getItem("currentCompany");

    return useMutation(
        async (values: CompanyDataRequest) => {
            const res = await axiosCoreInstance.put(`api/company/${currentCompany?.id}`, values);
            return res?.data || null;
        },
        {
            onSuccess: () => {
                if (currentCompany?.id) {
                    queryClient.invalidateQueries([RQ_COMPANY_DETAILS_KEY, currentCompany.id]);
                }
                queryClient.invalidateQueries([RQ_COMPANY_LIST_KEY]);

                if (onSuccess) {
                    onSuccess();
                }
            },
            onError: (
                err: AxiosError<{ errors?: FormErrorObject; message?: string; error?: string }>
            ) => {
                if (err.response?.data?.error) {
                    enqueueSnackbar(t("shared.validation.api." + err.response.data.error), {
                        variant: "error",
                    });
                }
                if (err.response?.data?.errors && onError) {
                    onError(err.response.data.errors);
                }
            },
        }
    );
};
